import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Flex, Box } from "reflexbox";
import { useLocation } from "@reach/router";
import useLocalStorage from "react-use/lib/useLocalStorage";

import { Button } from "./Button";
import { Container } from "./Container";
import * as colors from "../styles/colors";
import { cloudFunctionsBaseUrl } from "../services/firebase";

import { Modal } from "./Modal";

const Callout = styled(Flex)`
  margin-bottom: 80px;
  border-radius: 8px;
  position: relative;
  align-items: center;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const NEWSLETTER_POPUP_HASH = "#subscribe-to-newsletter";
const NEWSLETTER_POPUP_SHOW_AFTER_MS = 5000;

export function SubscribeToNewsletterModal({
  title,
  description,
  image = null,
  cta = "Subscribe",
  contentfulEntryId
}) {
  const [message, setMessage] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const location = useLocation();
  const [isPopupDismissed, setIsPopupDismissed] = useLocalStorage(
    "@neurosity/isNewsletterPopupDismissed",
    false
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      const isAlreadyShowingPopup =
        window.location.hash === NEWSLETTER_POPUP_HASH;

      if (!isPopupDismissed && !isAlreadyShowingPopup) {
        window.location.hash = NEWSLETTER_POPUP_HASH;
      }
    }, NEWSLETTER_POPUP_SHOW_AFTER_MS);

    return () => {
      clearTimeout(timeout);
    };
  }, [isPopupDismissed]);

  useEffect(() => {
    if (window.location.hash === NEWSLETTER_POPUP_HASH) {
      setShowModal(true);
    }
  }, [location, setShowModal]);

  async function onSubmit(email, onReset) {
    setLoading(true);

    await fetch(
      `${cloudFunctionsBaseUrl}/sendEmailFromContentfulEntry`,
      {
        method: "POST",
        mode: "cors",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email,
          contentfulEntryId
        })
      }
    )
      .then((response) => response.json())
      .then(async (data) => {
        console.log("data", data);
        if (data.ok) {
          setIsPopupDismissed(true);
          onReset();
          setStatus("success");
        } else {
          setStatus("error");
        }

        setMessage(data.message);
      })
      .catch((error) => {
        setStatus("error");
        setMessage(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function clearHash() {
    const url = new URL(window.location);
    url.hash = "";
    window.history.replaceState(null, document.title, url);
  }

  function closeModal() {
    setIsPopupDismissed(true);
    clearHash();
    setShowModal(false);
    setMessage(null);
    setStatus(null);
    setLoading(false);
  }

  return (
    <Modal
      variant="popup"
      isOpen={showModal}
      onRequestClose={closeModal}
      contentLabel={title}
    >
      <Container marginBottom={50}>
        <Callout>
          <Flex
            flexDirection={"column"}
            alignItems={"center"}
            textAlign={"center"}
            pb={[30]}
            pt={[40]}
            style={{
              color: colors.text
            }}
          >
            <Box
              fontSize={[22, 22, 28, 28]}
              mb={[3]}
              fontWeight={600}
              color={colors.primary}
            >
              {title}
            </Box>

            {status === "error" ? (
              <Box fontSize={[16]} color={colors.error}>
                {message}
              </Box>
            ) : (
              <Box
                fontSize={[18, 18, 22, 22]}
                width={[1, 0.8, 0.7, 0.7]}
              >
                {description}
              </Box>
            )}
          </Flex>

          {image ? (
            <Flex mb={[4]}>
              <img
                src={image}
                alt={title}
                title={title}
                style={{
                  height: "200px",
                  borderRadius: "6px",
                  mixBlendMode: "screen"
                }}
              />
            </Flex>
          ) : null}

          {!status || status === "error" ? (
            <EmailSignup
              onSubmit={onSubmit}
              cta={cta}
              loading={loading}
            />
          ) : (
            <Box fontSize={[16]} color={colors.success}>
              {message}
            </Box>
          )}

          {loading || status === "success" ? null : (
            <Flex mt={[4]}>
              <Box
                as="a"
                onClick={closeModal}
                fontSize={[14, 14, 16, 16]}
                style={{
                  cursor: "pointer"
                }}
              >
                No, thanks
              </Box>
            </Flex>
          )}
        </Callout>
      </Container>
    </Modal>
  );
}

function EmailSignup({ onSubmit, loading, cta }) {
  const [email, setEmail] = useState("");

  function onReset() {
    setEmail("");
  }

  return (
    <Flex
      as="form"
      flexDirection={["column", "row"]}
      justifyContent={"center"}
      alignItems={"center"}
      style={{ gap: ["16px"] }}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(email, onReset);
      }}
    >
      <Box
        as="input"
        p={["16px"]}
        width={[1]}
        minWidth={[250]}
        fontSize="14px"
        color={colors.text}
        type="email"
        name="email"
        label="email"
        aria-label="email"
        placeholder="Email address"
        value={email}
        disabled={loading}
        required={true}
        autoComplete="off"
        onChange={(event) => {
          setEmail(event.target.value);
        }}
        sx={{
          borderRadius: "8px",
          border: "1px solid transparent",
          boxShadow: "none",
          backgroundColor: colors.footerInputBackround,
          "::placeholder": {
            color: colors.dimmedLight
          },
          ":focus": {
            boxShadow: "none",
            border: `1px solid ${colors.mailBlue}`,
            color: colors.whiteLightColor
          }
        }}
      />
      <Box width={[1]}>
        <Button
          secondaryButton={true}
          type="submit"
          loading={loading}
          style={{
            width: "100%"
          }}
          more
        >
          {cta}
        </Button>
      </Box>
    </Flex>
  );
}
